<template>
  <div class="el-main">
    <LetterCompanyCard :current-card-id="currentCardId" />

    <LetterTextWithExamplesCard
      card-id="intro"
      :current-card-id="currentCardId"
      title="頭語と時候のあいさつ"
      description="頭語と時候のあいさつを入力しましょう"
      field-key="introSummary"
      error-key="introSummary"
      dialog-name="intro-dialog"
      :sentences="introSentences"
      :preview-errors="previewErrors"
    />

    <LetterTextWithExamplesCard
      card-id="entry-detail"
      :current-card-id="currentCardId"
      title="応募経緯"
      description="応募経緯を入力しましょう"
      field-key="entrySummary"
      error-key="entrySummary"
      dialog-name="entry-dialog"
      :sentences="entrySentences"
      :preview-errors="previewErrors"
    />

    <LetterResumeListCard
      :current-card-id="currentCardId"
      :preview-errors="previewErrors"
    />

    <LetterCreateDateCard :current-card-id="currentCardId" />

    <LetterConfirmCard :current-card-id="currentCardId" />

    <div
      class="card"
      v-show="currentCardId == 'output'"
      v-if="getMode != 'production1'"
    >
      <CompletedOutputPanel
        :formatId="formatId"
        :isSendYagiEmailEnabled="isSendYagiEmailEnabled"
        :mailPdfEnabled="mailPdfEnabled"
        :downloadPdfEnabled="downloadPdfEnabled"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onBeforeMount, onBeforeUnmount, ref, watch } from 'vue'
import { emitter } from '@/plugins/emitter'
import { useHostServices } from '@/composables/format-edit/useHostServices'
import { useFormatLocalData } from '@/composables/format-edit/useFormatLocalData'
import {
  createEmptyLetterFormatData,
  provideLetterFormatData
} from '@/composables/format-edit/useLetterFormatData'
import type { LetterExampleSentenceItem } from '@/components/format-edit/letter/LetterExampleSentenceDialog.vue'
import LetterCompanyCard from '@/components/format-edit/letter/LetterCompanyCard.vue'
import LetterTextWithExamplesCard from '@/components/format-edit/letter/LetterTextWithExamplesCard.vue'
import LetterResumeListCard from '@/components/format-edit/letter/LetterResumeListCard.vue'
import LetterCreateDateCard from '@/components/format-edit/letter/LetterCreateDateCard.vue'
import LetterConfirmCard from '@/components/format-edit/letter/LetterConfirmCard.vue'
import CompletedOutputPanel from '@/components/format-edit/completed/CompletedOutputPanel.vue'

const props = defineProps<{
  basicData: Record<string, unknown>
  formatId?: string
  previewFormat?: { errorMessages?: Record<string, string> } | null
  currentCardId?: string
  exampleSentences?: {
    intro?: LetterExampleSentenceItem[]
    entry?: LetterExampleSentenceItem[]
  }
}>()

const { formLib } = useHostServices()

const { formatData } = useFormatLocalData({
  saveName: 'letter',
  saveOtherName: 'rirekisho',
  createEmpty: createEmptyLetterFormatData
})
provideLetterFormatData(formatData)

const mailPdfEnabled = ref(true)
const downloadPdfEnabled = ref(true)
const isSendYagiEmailEnabled = ref(true)

const previewErrors = computed(() => {
  if (!props.previewFormat || !props.previewFormat.errorMessages) return {}
  return props.previewFormat.errorMessages
})

const getMode = computed(() => process.env.VUE_APP_MODE)

const introSentences = computed(() => props.exampleSentences?.intro || [])
const entrySentences = computed(() => props.exampleSentences?.entry || [])

function dataChanged() {
  mailPdfEnabled.value = true
  downloadPdfEnabled.value = true
}

watch(
  () => formatData.createDate,
  (newValue) => {
    if (newValue == null) return
    formatData.writeDate = formLib.dateFormat(
      newValue as Date,
      'yyyy年 m月 d日'
    )
  }
)

onBeforeMount(() => {
  if (formatData.createDate === null) {
    formatData.createDate = formLib.getToday()
  }
  emitter.on('basic-data-changed', dataChanged)
  emitter.on('format-data-changed', dataChanged)
})

onBeforeUnmount(() => {
  emitter.off('basic-data-changed', dataChanged)
  emitter.off('format-data-changed', dataChanged)
})
</script>

<style lang="scss" scoped>
* {
  list-style: none;
  box-sizing: border-box;
  margin: 0;
  padding: 0;
}

img {
  width: 100%;
  vertical-align: bottom;
}

.card-header h1,
.card-header h2 {
  padding-bottom: 1.5rem;
}
</style>
